<template>
    <div class="container pause-container">
        <div class="pause-text-container" style="margin: 24px 0px">
            <h2 class="pause-heading" v-text="$t('PAUSE_UNTIL')"></h2>
            <p>{{ $t('PAUSE_SELECT_DATE_TO_RESUME') }}</p>
        </div>

        <datepicker
            calendar-class="gifting__calendar"
            wrapper-class="calendar-wrapper"
            class="pause-calendar"
            :monday-first="true"
            :full-month-name="true"
            v-model="selectedRestartDate"
            :inline="true"
            :disabled-dates="allowedDates"/>

        <div v-if="selectedRestartDate" class="pause-info">
            <p class="mt-0 mb-0">{{ $t('PAUSE_INFO', { dateString } ) }}</p>
            <p class="mt-0 mb-0" v-html="$t('NEXT_BOX_RESTART', { nextDeliveryDate })"/>
        </div>

        <div class="pause-text-container">
            <p style="pause-before" v-text="$t('BEFORE_WE_PAUSE')"></p>
            <radio-field
                v-for="(item, index) in feedbackOptions"
                :key="index"
                v-model="selectedReason"
                :option="item.label"
                :label="item.label"/>
        </div>

        <div v-if="errorHandler.hasErrorMessage()" class="pause-error" v-text="errorHandler.getErrorMessage()"></div>
        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"/>:
                    <span class="font-normal" v-text="errorMsg"/>
                </li>
            </ul>
        </div>

        <div class="pause-btn-container">
            <button class="pause-btn" @click.prevent="pauseSubscription">
                <span v-if="processing" v-text="$t('SUBMIT_BUTTON_PLEASE_WAIT')"/>
                <span v-else v-text="$t('PAUSE_DELIVERIES_CONFIRM')"/>
            </button>
            <div class="back">
                <a @click.prevent="goBack()">
                    <u>{{ $t('PAUSE_BACK')}}</u>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import { addWeeks, formatDate, isDateAfter } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import { CSPageEvents } from '@/helpers/GAHelpers';
import RadioField from '@/components/forms/fields/RadioField';

let subscriptionService = new SubscriptionService();

export default {
    name: 'Pause',
    components: {
        Datepicker,
        RadioField,
    },
    data() {
        return {
            processing: false,
            selectedReason: null,
            selectedRestartDate: null,
            errorHandler: new ErrorResponseHandler(),
            stageId: 'pause',
            nextDeliveryDate: null,
        };
    },
    computed: {
        ...mapGetters({
            subscription: 'subscription/getActiveSubscription',
            feedbackOptions: 'account/getPauseFeedbackOptions',
            allDeliveries: 'scheduledOrder/getAllDeliveries',
            nextAvailableDelivery: 'scheduledOrder/getNextAvailableDeliveryDatePredicted',
        }),
        allowedDates() {
            let today = new Date();
            return {
                to: today,
                from: addWeeks(today, 7),
            };
        },
        dateString() {
            let formattedSelectedDate = formatDate(this.selectedRestartDate, Dates.DASHED_YEAR_MONTH_DAY);
            let upcomingDates = this.allDeliveries.filter(item => {
                return isDateAfter(item.datetime, formattedSelectedDate);
            });
            this.nextDeliveryDate = formatDate(upcomingDates[0].datetime, Dates.DATE_MONTH);
            return formatDate(this.selectedRestartDate, Dates.DATE_MONTH);
        },
    },
    methods: {
        pauseSubscription() {
            if (!this.selectedReason) {
                this.errorHandler.setResponse({ error: this.$t('PAUSE_ERR') });
                return;
            }
            CSPageEvents(`${this.stageId}-accepted`);
            this.processing = true;
            let formattedDate = formatDate(this.selectedRestartDate, Dates.DASHED_YEAR_MONTH_DAY);
            subscriptionService.pausedUntil({
                subscriptionId: this.subscription?.id,
                deliveryDate: formattedDate,
                feedback: this.selectedReason,
            }).then(() => {
                this.processing = false;
                this.handleDeliveriesPaused();
            })
                .catch(error => {
                    this.processing = false;
                    this.errorHandler.setResponse(error.response.data);
                });
        },

        handleDeliveriesPaused() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', this.$t('PAUSE_DELIVERIES_CONFIRMATION_MODAL_YOUR_DELIVERIES'));
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            return this.goBack();
        },
        goBack() {
            return this.$router.go(-1);
        },
    },
    watch: {
        selectedReason(value) {
            if (value && this.errorHandler) {
                this.errorHandler = new ErrorResponseHandler();
            }
        },
    },
};
</script>

<style>
    .pause-container, .pause-info, .pause-error {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .calendar-wrapper, .pause-error, .day__month_btn {
        font-family: 'Gill Sans';
        font-style: normal;
    }

    .pause-container {
        padding: 0 16px;
        margin: 12px auto;
    }

    .pause-heading {
        margin-bottom: 23px;
    }

    .pause-text-container {
        margin-top: 24px;
        align-self: flex-start;
        text-align: left;
    }

    .pause-btn-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
    }

    .pause-btn {
        margin: 38px 0 0;
        color: #FFFFFF;
        background: #262323;
        height: 40px;
        padding-top: 0px;
        padding-bottom: 0px;
        width: 167px;
        border-radius: 4px;
    }

    .pause-calendar {
        width: 370px;
        height: 370px;
        padding: 20px;
        border: 1px solid #CECECE;
    }

    .calendar-wrapper {
        width: 370px;
        height: 380px;
        padding: 16px;
        font-weight: 300;
        font-size: 20px;
        line-height: 24px;
    }

    .day__month_btn {
        font-weight: 400;
        font-size: 18px;
        line-height: 20px;
        margin-top: 12px;
    }

    .pause-info {
        padding: 12px;
        background: #ECE9E8;
        border-radius: 8px;
        width: 100%;
        margin-top: 40px;
    }

    .pause-error {
        width: 100%;
        min-height: 48px;
        color: #AE4743;
        border: 2px solid #AE4743;
        border-radius: 8px;
        margin-top: 24px;
        padding: 11px;
        font-weight: 300;
        font-size: 18px;
        line-height: 20px;
    }

    .pause-before {
        margin-bottom: 25px;
    }

    .back {
        margin-top: 16px;
        margin-bottom: 48px;
        cursor: pointer;
    }

@media (min-width: 480px) {
        .pause-container {
            max-width: 612px;
        }

        .pause-btn-container {
            display: block;
            width: 100%;
        }

        .pause-calendar {
            width: 370px;
            height: 402px;
            padding: 20px;
            box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.2);
            border: none;
        }

        .pause-error {
            height: 48px;
        }
    }
</style>
